import { RootState, store } from '@/app/store';
import { selectAllPortfolioBalances } from '@/features/balance/balancesApi';
import { selectAllMarkets } from '@/features/markets/marketsApi';
import Icon from '@/shared/components/Icon/Icon';
import { useWindowDimensions } from '@/shared/hooks/useWindowDimension';
import { ASSETS_NAMES } from '@/shared/utils/constants';
import { IonBackButton, IonButtons, IonHeader, IonToolbar } from '@ionic/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import DesktopHeader from '../DesktopHeader/DesktopHeader';
import MobileHeader from '../MobileHeader/MobileHeader';
import styles from './Header.module.css';
import { useSelector } from 'react-redux';
import { selectIsLoggedIn } from '@/features/oAuth/oAuthSlice';
import { selectCryptoDepositsNeedingTravelRule, useGetCryptoDepositsQuery } from '@/features/crypto/cryptoApi';

type Props = {
  mobileMainPage?: boolean;
  name?: string;
};

const isNotLandingPage = (location: string) => {
  return location.toString() !== '/' && location !== '';
};
const Header: React.FC<Props> = ({ mobileMainPage = false, name }) => {
  const location = useLocation();

  const isLoggedIn = useSelector(selectIsLoggedIn);

  const {} = useGetCryptoDepositsQuery(undefined, {
    skip: !isLoggedIn
  });

  const allDepositsRequiringTravelRule = useSelector((state: RootState) =>
    selectCryptoDepositsNeedingTravelRule(state)
  );

  const showNotificationForTravelRule = allDepositsRequiringTravelRule.length > 0;

  const { t } = useTranslation();
  const { isMobileView } = useWindowDimensions();

  const allMarkets = selectAllMarkets(store.getState());

  const assetsPortfolioBalances = selectAllPortfolioBalances(store.getState());
  const showBackArrow = isNotLandingPage(location.pathname);
  const selectedMarket = useMemo(
    () =>
      allMarkets?.find(({ id }) => {
        return location.pathname.includes(id);
      }),
    [location.pathname, allMarkets]
  );

  const selectedAsset = useMemo(
    () =>
      assetsPortfolioBalances?.find(({ assetId }) => {
        return location.pathname.includes(assetId);
      }),
    [location.pathname, assetsPortfolioBalances]
  );

  const nonAssetTitle = {
    'profile-page': t('general.profile'),
    'help-page': t('general.help.info'),
    'promo-page': t('settings.promo'),
    'email-page': t('data.email'),
    'phone-page': t('data.mobile'),
    'password-page': t('data.password'),
    'taxnumber-page': t('settings.taxnumber'),
    'devices-page': t('settings.log_in_and_security.user_sessions.table_title'),
    'ibanAccount-page': t('settings.account'),
    'transactionHistory-page': t('settings.taxId.reports')
  }[name || ''];

  return isMobileView ? (
    <IonHeader slot="fixed" className={styles.headerMobile}>
      {!mobileMainPage && showBackArrow && (
        <IonToolbar>
          <IonButtons slot="start" className={styles.backButton}>
            <IonBackButton icon="assets/icons/arrow-back.svg" text="" defaultHref="/" />
          </IonButtons>

          <div className={styles.headerContent}>
            {selectedMarket ? (
              <>
                <Icon
                  src={`assets/icons/${selectedMarket.base.id}.svg`}
                  color={selectedMarket.base.id}
                  size="large"
                  className={styles.icon}
                />
                <strong>{selectedMarket.base.name}</strong>
              </>
            ) : selectedAsset ? (
              <>
                <Icon
                  src={`assets/icons/${
                    selectedAsset?.assetId === 'eur'
                      ? ASSETS_NAMES[selectedAsset?.assetId || '']
                      : selectedAsset?.assetId
                  }.svg`}
                  color={selectedAsset?.assetId}
                  size="large"
                  className={styles.icon}
                />
                <strong>{selectedAsset ? `${ASSETS_NAMES[selectedAsset?.assetId || '']} Wallet` : ''}</strong>
              </>
            ) : (
              <strong>{nonAssetTitle}</strong>
            )}
          </div>
        </IonToolbar>
      )}
      {mobileMainPage && <MobileHeader name={name} />}
    </IonHeader>
  ) : (
    <DesktopHeader
      showNotificationForTravelRule={showNotificationForTravelRule}
      notificationCount={allDepositsRequiringTravelRule.length || 0}
    />
  );
};

export default Header;
